import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import QRCode from "@/design/components/QRCode";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputPassword from "@/design/forms/InputPassword";
import InputText from "@/design/forms/InputText";
import StringForm from "@/design/forms/StringForm";
import { showSnackbar } from "@/store/reducers/app";
import { setAuth } from "@/store/reducers/auth";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Sesame from "@/utils/Sesame";
import { getAccessLevel, toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viCheck,
  viCopy,
  viPassword,
  viPerson,
  viShieldKeyhole,
  viShieldPlus,
  viShieldXmark,
  viSpinnerThird,
  viMin,
  viXmark,
} from "@sippy-platform/valkyrie";

import { OTPField } from "@base-ui/react/otp-field";
import { Button, Card, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";

import ChangeEmail from "../../backstage/accounts/ChangeEmail";
import accountReducer from "../../domain/pbx/accountReducer";

export default function Profile() {
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const auth = useSelector((state) => state.auth);
  const pbx = useSelector((state) => state.domain.pbx);

  const {
    value: account,
    dispatch: accountDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: auth,
    reducer: accountReducer,
    validator: () => ({
      firstname: string().required({ message: "First name is required." }).length([1, 40], {
        message: "First name must be at most 40 characters.",
      }),
      lastName: string().length([1, 40], {
        message: "Last name must be at most 40 characters.",
      }),
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "Email address isn't a valid format." }),
      password: string()
        .required({
          message: "Password is required.",
          condition: changePassword,
        })
        .length([8], {
          message: "Password must be at least 8 characters.",
          condition: changePassword,
        })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password should have at least one letter and one number.",
        })
        .is(account.confirmPassword, {
          message: "Passwords do not match.",
          condition: changePassword,
        }),
    }),
    onSubmit: () => Api.storePbxAccount(account, pbx),
    onSuccess: () => dispatch(setAuth(account)),
  });

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getAccount({ id: auth.id }))
      .then((response) => {
        setChangePassword(false);
        accountDispatch({ type: "set", value: response.data.account });
      })
      .finally(() => toggleLoadingState(false));
  }, [auth.id, accountDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    accountDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  const generatePassword = () => {
    const nextPassword = new Sesame(16).generate();
    accountDispatch({ type: "resetPassword", value: nextPassword });
    setShowPassword(true);
    setSaveState(SaveState.UNSAVED);
  };

  function cancelChangePassword() {
    setChangePassword(false);
    accountDispatch({ type: "resetPassword", value: undefined });
    setShowPassword(false);
  }

  // 2FA
  const [twoStepAuthChallenge, setTwoStepAuthChallenge] = useState({});
  const [verificationCode, setVerificationCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState({});
  const [totpView, setTotpView] = useState(null);
  const [pendingTfaCreation, setPendingTfaCreation] = useState(false);
  const [pendingTfaConfirmation, setPendingTfaConfirmation] = useState(false);
  const [pendingTfaDeletion, setPendingTfaDeletion] = useState(false);

  function cancelTotp() {
    setTwoStepAuthChallenge({});
    setVerificationCode("");
    setRecoveryCodes({});
    setTotpView(null);
    setPendingTfaCreation(false);
    setPendingTfaConfirmation(false);
    setPendingTfaDeletion(false);
  }

  function enableTotp() {
    setPendingTfaCreation(true);

    Promise.resolve(Api.getAccountTotp())
      .then((response) => {
        setTotpView("enable");
        setTwoStepAuthChallenge(response.data.totp);
      })
      .finally(() => setPendingTfaCreation(false));
  }

  function confirmTotp() {
    setPendingTfaConfirmation(true);

    Promise.resolve(
      Api.postAccountTotp({
        code: verificationCode,
        secret: twoStepAuthChallenge.secret,
      }),
    )
      .then((response) => {
        setTotpView("recovery");
        setRecoveryCodes(response.data.totp.recoveryCodes);
        accountDispatch({ type: "twoStepAuthEnabled", value: true });
      })
      .finally(() => {
        setPendingTfaConfirmation(false);
        setVerificationCode("");
      });
  }

  function disableTotp() {
    setTotpView("disable");
  }

  function deleteTotp() {
    setPendingTfaDeletion(true);

    Promise.resolve(Api.deleteAccountTotp())
      .then(() => {
        accountDispatch({ type: "twoStepAuthEnabled", value: false });
      })
      .finally(() => {
        setPendingTfaDeletion(false);
        setTotpView(null);
      });
  }

  const copyRecoveryCodesToClipboard = () => {
    navigator.clipboard
      .writeText(recoveryCodes.join("\n"))
      .then(() => {
        dispatch(showSnackbar("Recovery codes copied to clipboard."));
      })
      .catch(() => {
        dispatch(showSnackbar("Could not copy recovery codes to the clipboard."));
      });
  };

  return (
    <Stack spacing={2}>
      <HeaderCard
        avatar={<Avatar text={`${account.firstname ?? ""} ${account.lastname ?? ""}`} />}
        action={<SaveIndicator formState={formState} errors={allErrors} save={save} />}
        title={
          account.firstname || account.lastname ? `${account.firstname ?? ""} ${account.lastname ?? ""}` : "New account"
        }
        subheader={account.email || ""}
      />
      <FormGroup title="Identity" actions={<ChangeEmail account={account} />}>
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputText
            required
            id="firstname"
            label="First name"
            onChange={formHandler}
            value={account.firstname}
            error={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputText id="lastname" label="Last name" onChange={formHandler} value={account.lastname} error={errors} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputText
            required
            disabled
            id="email"
            label="Email address"
            onChange={formHandler}
            value={account.email}
            error={errors}
          />
        </Grid>
      </FormGroup>
      <FormGroup
        title="Password"
        actions={
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            {changePassword ? (
              <Button
                variant="contained"
                onClick={cancelChangePassword}
                startIcon={<Valkyrie icon={account.id ? viXmark : viPerson} />}
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setChangePassword(true)}
                startIcon={<Valkyrie icon={viPassword} />}
              >
                Change password
              </Button>
            )}
          </Stack>
        }
      >
        {changePassword && (
          <>
            <Grid size={12}>
              <Button
                variant="outlined"
                onClick={() => generatePassword()}
                startIcon={<Valkyrie icon={viShieldKeyhole} />}
              >
                Generate password
              </Button>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2">
                The new password cannot be seen after you have left this page. A password has to have at least 8
                characters with a combination of letters and numbers.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputPassword
                required
                showPasswordStrength
                showPassword={showPassword}
                id="password"
                label="Password"
                onChange={formHandler}
                value={account.password}
                error={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputPassword
                required
                id="confirmPassword"
                label="Confirm password"
                onChange={formHandler}
                value={account.confirmPassword}
                error={errors}
              />
            </Grid>
          </>
        )}
      </FormGroup>
      <FormGroup
        title="2 Factor Authentication"
        actions={
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            {account.twoStepAuthEnabled && totpView !== "disable" && (
              <Button color="error" onClick={disableTotp} startIcon={<Valkyrie icon={viShieldXmark} />}>
                Delete 2FA
              </Button>
            )}
            {!account.twoStepAuthEnabled && totpView !== "enable" && (
              <Button
                variant="contained"
                onClick={enableTotp}
                startIcon={
                  <Valkyrie icon={pendingTfaCreation ? viSpinnerThird : viShieldPlus} spin={pendingTfaCreation} />
                }
                disabled={pendingTfaCreation}
              >
                Enable 2FA
              </Button>
            )}
            {((totpView === "enable" && !account.twoStepAuthEnabled) ||
              (totpView === "disable" && account.twoStepAuthEnabled)) && (
              <Button variant="contained" onClick={cancelTotp} startIcon={<Valkyrie icon={viXmark} />}>
                Cancel
              </Button>
            )}
          </Stack>
        }
      >
        {totpView === "enable" ? (
          <>
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Scan the QR code with your authenticator app to add My Sippy. Then fill in the code generated by your
                app to confirm.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <QRCode value={twoStepAuthChallenge.uri} />
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Secret: {twoStepAuthChallenge.secret}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <div className="flex w-full max-w-80 flex-col items-start gap-1">
                <label htmlFor="otp" className="text-xs font-normal text-zinc-500">
                  Verification code *
                </label>
                <OTPField.Root
                  id="otp"
                  length={6}
                  className="flex w-full items-center gap-1"
                  value={verificationCode}
                  onValueChange={setVerificationCode}
                  disabled={pendingTfaConfirmation}
                >
                  <div className="flex">
                    {Array.from({ length: 3 }, (_, index) => (
                      <OTPField.Input
                        key={index}
                        autoFocus={index === 0}
                        className="font-inherit m-0 box-border h-10 w-10 border border-zinc-950/20 bg-transparent text-center text-lg/5 font-medium outline-none not-last:border-e-0 first:rounded-s-md last:rounded-e-md focus:z-1 focus:outline-2 focus:-outline-offset-1 focus:outline-[#0068d8] focus:outline-solid dark:border-zinc-700"
                        aria-label={`Character ${index + 1} of 6`}
                      />
                    ))}
                  </div>
                  <OTPField.Separator className="flex grow items-center justify-center text-zinc-600">
                    <Valkyrie icon={viMin} />
                  </OTPField.Separator>
                  <div className="flex">
                    {Array.from({ length: 3 }, (_, index) => (
                      <OTPField.Input
                        key={index + 3}
                        className="font-inherit m-0 box-border h-10 w-10 border border-zinc-950/20 bg-transparent text-center text-lg/5 font-medium outline-none not-last:border-e-0 first:rounded-s-md last:rounded-e-md focus:z-1 focus:outline-2 focus:-outline-offset-1 focus:outline-[#0068d8] focus:outline-solid dark:border-zinc-700"
                        aria-label={`Character ${index + 4} of 6`}
                      />
                    ))}
                  </div>
                </OTPField.Root>
              </div>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  onClick={confirmTotp}
                  startIcon={
                    <Valkyrie icon={pendingTfaConfirmation ? viSpinnerThird : viCheck} spin={pendingTfaConfirmation} />
                  }
                  disabled={verificationCode.length !== 6 || pendingTfaConfirmation}
                >
                  Confirm
                </Button>
              </Stack>
            </Grid>
          </>
        ) : totpView === "recovery" ? (
          <>
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                These are your recovery codes to get back into your account if you loose access to your authenticator
                app. Store them safely, you won't be able to view them again after leaving your profile.
              </Typography>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  pr: 8.5,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(10ch, 100%), 1fr))",
                  backgroundColor: "background.default",
                  gap: ".75rem 2rem",
                  position: "relative",
                }}
              >
                {recoveryCodes.map((code) => (
                  <Typography sx={{ fontFamily: "monospace", lineHeight: 1 }} key={code}>
                    {code}
                  </Typography>
                ))}
                <Tooltip title="Copy recovery codes to clipboard">
                  <IconButton
                    onClick={copyRecoveryCodesToClipboard}
                    aria-label="Copy recovery codes"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  >
                    <Valkyrie icon={viCopy} />
                  </IconButton>
                </Tooltip>
              </Card>
            </Grid>
          </>
        ) : totpView === "disable" ? (
          <>
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Are you sure you want to remove two factor authentication from your account?
              </Typography>
              <Stack direction="row" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={deleteTotp}
                  disabled={pendingTfaDeletion}
                  startIcon={
                    <Valkyrie icon={pendingTfaDeletion ? viSpinnerThird : viShieldXmark} spin={pendingTfaDeletion} />
                  }
                >
                  Confirm
                </Button>
              </Stack>
            </Grid>
          </>
        ) : null}
      </FormGroup>
      <FormGroup title="Details">
        {account.createdDate && account.createdDate !== "0000-00-00" && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StringForm label="Joined" text={format(parseISO(account.createdDate), "d MMMM yyyy")} />
          </Grid>
        )}
        {account.updatedDate && account.updatedDate !== "0000-00-00" && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StringForm label="Last changed" text={format(parseISO(account.updatedDate), "d MMMM yyyy")} />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StringForm label="Type" text={getAccessLevel(account.accessLevel)} />
        </Grid>
      </FormGroup>
    </Stack>
  );
}
